import {
  Box,
  Typography,
  TextField,
  Paper,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Gavel as GavelIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { useNavigate, useLocation } from 'react-router-dom';
import StatusChip from '../components/StatusChip';
import { CaseStatus } from '../utils/caseStatusStyles';
import { Case, Counsel as _Counsel } from './CaseList';

// Extend Case type to include has_reviewed, my_review_score, and avg_score
type ReviewCase = Case & {
  has_reviewed: boolean;
  my_review_score?: number | null;
  avg_score?: number | null;
};

const dataGridBoldHeaders = {
  backgroundColor: 'white',
  borderRadius: 0,
  border: '1px solid',
  borderColor: 'divider',
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
    backgroundColor: 'action.hover',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    paddingTop: '4px',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    padding: '16px 16px',
    fontSize: '0.9375rem',
  },
  '& .MuiDataGrid-cell:first-of-type': {
    paddingLeft: '24px',
  },
  '& .MuiDataGrid-cell:last-of-type': {
    paddingRight: '24px',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f5f5',
    borderRadius: 0,
    borderBottom: '3px solid #9e9e9e',
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#f5f5f5',
      padding: '16px 16px 12px 16px',
      minHeight: '64px !important',
      height: 'auto !important',
      maxHeight: 'none !important',
    },
    '& .MuiDataGrid-columnHeader:first-of-type': {
      paddingLeft: '24px',
    },
    '& .MuiDataGrid-columnHeader:last-of-type': {
      paddingRight: '24px',
    },
    '& .MuiDataGrid-columnHeaderTitleContainerContent': {
      overflow: 'visible',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      color: 'text.primary',
      fontWeight: 700,
      fontSize: '0.875rem',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      lineHeight: 1.2,
      whiteSpace: 'normal',
      wordWrap: 'break-word',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: '0 4px',
    },
    '& .MuiDataGrid-iconButtonContainer': {
      '& .MuiIconButton-root': {
        color: 'text.secondary',
        '&:hover': {
          backgroundColor: 'action.hover',
          color: 'text.primary',
        },
      },
    },
    '& .MuiDataGrid-sortIcon': {
      color: 'text.secondary',
    },
    '& .MuiDataGrid-menuIcon': {
      color: 'text.secondary',
    },
  },
  '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderTopColor: 'divider',
    backgroundColor: '#fafafa',
  },
};

export default function ReviewList() {
  const { access } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [cases, setCases] = useState<ReviewCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState('');

  // Load review filter from localStorage on mount
  const [reviewFilter, setReviewFilter] = useState<'all' | 'pending' | 'completed'>(() => {
    try {
      const saved = localStorage.getItem('reviewListFilter');
      return (saved as 'all' | 'pending' | 'completed') || 'all';
    } catch {
      return 'all';
    }
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // Stats from API
  const [stats, setStats] = useState({
    total_cases: 0,
    reviewed_cases: 0,
    pending_reviews: 0,
  });

  const headers = { Authorization: `Bearer ${access}` };

  // Use stats from API instead of calculating from paginated results
  const totalCases = stats.total_cases;
  const reviewedCases = stats.reviewed_cases;
  const pendingReviews = stats.pending_reviews;

  // Save review filter to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('reviewListFilter', reviewFilter);
    } catch (err) {
      console.error('Failed to save review filter to localStorage', err);
    }
  }, [reviewFilter]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      };
      if (search) params.search = search;
      // Add review filter to backend query
      if (reviewFilter === 'pending') {
        params.has_reviewed = 'false';
      } else if (reviewFilter === 'completed') {
        params.has_reviewed = 'true';
      }

      // Add sorting parameter
      if (sortModel.length > 0) {
        const sort = sortModel[0];
        const orderingField = sort.field;
        params.ordering = sort.sort === 'desc' ? `-${orderingField}` : orderingField;
      }

      console.log('Fetching review-list with params:', params);
      const res = await api.get('/review-list/', { headers, params });
      console.log('Review List API response:', res.data);
      setCases(res.data.results ?? res.data);
      // Extract stats from API response
      if (res.data.stats) {
        setStats(res.data.stats);
      }
      // Set total row count for server-side pagination
      if (res.data.count !== undefined) {
        setRowCount(res.data.count);
      }
    } catch (err) {
      console.error('Failed to fetch review list', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access, paginationModel.page, paginationModel.pageSize, reviewFilter, location.pathname, sortModel]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Grid columns
  const columns: GridColDef<ReviewCase>[] = [
    // 1. Review Status
    {
      field: 'has_reviewed',
      headerName: 'Review Status',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {params.value ? (
            <>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                Reviewed
              </Typography>
            </>
          ) : (
            <>
              <UncheckedIcon sx={{ color: 'warning.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
                Pending
              </Typography>
            </>
          )}
        </Box>
      ),
    },
    // 2. Lead Defendant
    {
      field: 'lead_defendant',
      headerName: 'Lead Defendant',
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            '&:hover': { color: 'primary.main' }
          }}
        >
          {params.value || '-'}
        </Typography>
      )
    },
    // 3. Alleged Fraud Amount
    {
      field: 'total_billed',
      headerName: 'Alleged Fraud Amount',
      width: 240,
      type: 'number',
      valueGetter: (value, row) => parseFloat(row.total_billed ?? '0'),
      renderCell: (params) => {
        const val = params.value;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {typeof val === 'number' && !isNaN(val) && val > 0
              ? formatCurrency(val)
              : '-'}
          </Typography>
        );
      },
    },
    // 7. States
    {
      field: 'states',
      headerName: 'States',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const states = params.row?.states;
        if (!states || states.length === 0) {
          return (
            <Typography variant="body2" color="text.disabled">
              -
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {states.map((state) => (
              <Chip
                key={state}
                label={state}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: 'primary.50',
                  borderColor: 'primary.200',
                  color: 'primary.700',
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    padding: '0 6px',
                  }
                }}
              />
            ))}
          </Box>
        );
      }
    },
    // 8. Districts
    {
      field: 'districts',
      headerName: 'Districts',
      flex: 2.5,
      minWidth: 350,
      sortable: false,
      renderCell: (params) => {
        const districts = params.row?.districts;
        if (!districts || districts.length === 0) {
          return (
            <Typography variant="body2" color="text.disabled">
              -
            </Typography>
          );
        }

        const displayDistricts = districts.slice(0, 6);
        const remainingCount = districts.length - 6;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {displayDistricts.map((district: { district: string; count: number }) => (
              <Chip
                key={district.district}
                label={`${district.district} (${district.count})`}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: 'secondary.50',
                  borderColor: 'secondary.200',
                  color: 'secondary.700',
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    padding: '0 6px',
                  }
                }}
              />
            ))}
            {remainingCount > 0 && (
              <Chip
                label={`+${remainingCount} more`}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: 'grey.100',
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    padding: '0 6px',
                  }
                }}
              />
            )}
          </Box>
        );
      }
    },
    // 9. Review Count
    {
      field: 'review_count',
      headerName: 'Reviews',
      width: 100,
      type: 'number',
      renderCell: (params) => {
        const count = params.value || 0;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {count}
          </Typography>
        );
      },
    },
    // 10. My Review Score
    {
      field: 'my_review_score',
      headerName: 'My Review',
      width: 110,
      type: 'number',
      renderCell: (params) => {
        const score = params.value;
        if (score === null || score === undefined) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {score}
          </Typography>
        );
      },
    },
    // 11. Average Score
    {
      field: 'avg_score',
      headerName: 'Avg Score',
      width: 110,
      type: 'number',
      renderCell: (params) => {
        const score = params.value;
        if (score === null || score === undefined) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {score.toFixed(1)}
          </Typography>
        );
      },
    },
    // 12. Status
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      minWidth: 140,
      renderCell: (params) => {
        return (
          <StatusChip
            status={params.value as CaseStatus}
            size="small"
            sx={{ minWidth: 90 }}
          />
        );
      }
    },
  ];

  return (
    <Layout>
      {/* Stats Cards */}
      <Box
        display="flex"
        gap={3}
        mb={4}
        sx={{
          flexWrap: 'wrap',
          '& > *': {
            flex: '1 1 250px',
            minWidth: '200px',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Card
          sx={{
            backgroundColor: '#e3f2fd',
            display: 'flex',
            flex: 1,
            borderLeft: '4px solid #1976d2',
            border: '1px solid',
            borderColor: '#bbdefb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeftColor: '#1565c0',
              transform: 'translateY(-2px)',
            }
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flex: 1, py: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                Total Cases for Review
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {totalCases}
              </Typography>
            </Box>
            <GavelIcon sx={{ fontSize: 48, color: '#1976d2', opacity: 0.2 }} />
          </CardContent>
        </Card>

        <Card
          sx={{
            backgroundColor: '#e8f5e9',
            display: 'flex',
            flex: 1,
            borderLeft: '4px solid #2e7d32',
            border: '1px solid',
            borderColor: '#c8e6c9',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeftColor: '#1b5e20',
              transform: 'translateY(-2px)',
            }
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flex: 1, py: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                Reviews Completed
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {reviewedCases}
              </Typography>
            </Box>
            <ReviewIcon sx={{ fontSize: 48, color: '#2e7d32', opacity: 0.2 }} />
          </CardContent>
        </Card>

        <Card
          sx={{
            backgroundColor: '#fff3e0',
            display: 'flex',
            flex: 1,
            borderLeft: '4px solid #ed6c02',
            border: '1px solid',
            borderColor: '#ffe0b2',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeftColor: '#e65100',
              transform: 'translateY(-2px)',
            }
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flex: 1, py: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                Pending Reviews
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {pendingReviews}
              </Typography>
            </Box>
            <UncheckedIcon sx={{ fontSize: 48, color: '#ed6c02', opacity: 0.2 }} />
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderBottom: '2px solid #e0e0e0',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search cases..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') fetchCases();
              }}
              sx={{
                minWidth: 250,
                backgroundColor: 'background.default',
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={reviewFilter}
                onChange={(e) => setReviewFilter(e.target.value as 'all' | 'pending' | 'completed')}
                label="Filter"
                sx={{
                  backgroundColor: 'background.default',
                }}
              >
                <MenuItem value="all">All Reviews</MenuItem>
                <MenuItem value="pending">Pending Reviews</MenuItem>
                <MenuItem value="completed">Completed Reviews</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Data Table */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderTop: 'none',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          mb: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}
      >
        <DataGrid
          rows={cases}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 25, 50]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => setSortModel([...newSortModel])}
          disableRowSelectionOnClick
          disableVirtualization
          autoHeight
          onRowClick={(params) => {
            const caseRow = params.row as ReviewCase;
            navigate(`/cases/${caseRow.id}`);
          }}
          sx={{
            ...dataGridBoldHeaders,
            width: '100%',
          }}
        />
      </Paper>
    </Layout>
  );
}